import "./TableWrapperComponent.scss";

import * as React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {IAPIResponseType} from "../../models/api.model";
import {ITableComponentProps} from "../../models/table.model";
import {CommonService} from "../../services";
import PaginationComponent from "../pagination/PaginationComponent";
import _, {debounce, isEqual} from "lodash";
import TableComponent from "../table/TableComponent";
import {AXIOS_REQUEST_CANCELLED} from "../../services/api.service";

export interface TableComponentProps extends ITableComponentProps {
    moduleName?: string;
    type?: "ant" | "custom",
    url: string,
    method: "get" | "post" | string,
    isPaginated?: boolean,
    extraPayload?: any;
    refreshToken?: string; // TODO review and make it standard
    autoHeight?: boolean;
    onDataLoaded?: any;
}

const TableWrapperComponent = (props: TableComponentProps) => {
    const {refreshToken, moduleName, autoHeight, id, url, method, extraPayload, onDataLoaded, ...otherProps} = props;
    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    // const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
    const [isDataLoadingFailed, setIsDataLoadingFailed] = useState<boolean>(false);
    const [data, setData] = useState<any>([]);
    const pageNumRef = useRef<number>(0);
    const totalResultsRef = useRef<number>(0);
    const pageSizeRef = useRef<number>(25);
    const isPaginated = props.isPaginated !== undefined ? props.isPaginated : true;
    const type = props.type || "custom";
    const APICallSubscription = useRef<any>(null);
    const retryCount = useRef<number>(0);

    const getListData = useCallback(() => {
        const cancelTokenSource = CommonService.getCancelToken();
        const payload = _.cloneDeep({limit: pageSizeRef.current, ...extraPayload, page: pageNumRef.current + 1});
        if (payload?.sort && payload?.sort?.key) { // TODO to make sort more consistent
            payload.sort[payload.sort.key] = payload?.sort?.order;
            delete payload.sort.key;
            delete payload.sort.order;
        } else {
            delete payload.sort;
        }
        if(!isPaginated){
            delete payload.limit;
            delete payload.page;
        }
        let apiCall: any;
        if (method === "post") {
            apiCall = CommonService._api.post;
        } else {
            apiCall = CommonService._api.get;
        }
        if (APICallSubscription && APICallSubscription.current) {
            APICallSubscription.current.cancel();
        }
        APICallSubscription.current = cancelTokenSource;
        setIsDataLoading(true);
        setIsDataLoadingFailed(false);
        let listData: any[] = [];
        debounce(() => {
            apiCall(url, payload, {}, {cancelToken: cancelTokenSource.token}).then((response: IAPIResponseType<any>) => {
                if (response.data) {
                    if (isPaginated) {
                        if (response?.data?.docs) {
                            listData = response?.data?.docs || [];
                        } else if (response?.data?.records) {
                            listData = response?.data?.records || [];
                        } else {
                            listData = response?.data || []
                        }

                        retryCount.current = 0;
                        totalResultsRef.current = response?.data?.total || response?.data?.totalPages || 0;

                    } else {
                        listData = response?.data;
                        onDataLoaded && onDataLoaded(listData);
                    }
                }
                setData(listData.map((row, rowIndex) => ({
                    ...row,
                    rowIndex,
                    sNo: (((payload.page - 1) * payload.limit) + rowIndex + 1)
                })));
                setIsDataLoading(false);
                setIsDataLoadingFailed(false);
            }).catch((error: any) => {
                if (error.reason !== AXIOS_REQUEST_CANCELLED) { // if previous request got cancelled do not close loading state
                    setData(listData);
                    setIsDataLoading(false);
                    setIsDataLoadingFailed(true);
                }
            })
        }, 1000)();
        //eslint-disable-next-line
    }, [ isPaginated, method, url, extraPayload]);

    useEffect(() => {
        if (extraPayload?.page) {
            pageNumRef.current = extraPayload.page - 1;
        }
        getListData();
    }, [getListData, refreshToken, extraPayload]);

    const handlePageNumberChange = useCallback((event: unknown, newPage: number) => {
        pageNumRef.current = newPage;
        getListData();
    }, [getListData]);

    const handlePageSizeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        pageSizeRef.current = +event.target.value;
        getListData();
    }, [getListData]);

    useEffect(() => {
        const sub = CommonService._communications.TableWrapperRefreshSubject.subscribe((data) => {
            if (data.moduleName === moduleName) {
                getListData();
            }
        });
        return () => {
            sub.unsubscribe();
        }
    }, [getListData, moduleName]);

    return (
        <>
            {
                type === "custom" && <TableComponent
                    loading={isDataLoading}
                    errored={isDataLoadingFailed}
                    data={data}
                    id={id}
                    sort={extraPayload?.sort}
                    {...otherProps}
                />
            }
            {
                (data && isPaginated) && <PaginationComponent
                    paginationOptions={[10, 25, 100]}
                    totalResults={totalResultsRef.current}
                    limit={pageSizeRef.current}
                    page={pageNumRef.current}
                    onPageChange={handlePageNumberChange}
                    onRowsPerPageChange={handlePageSizeChange}
                    id={id + "_pagination"}
                />
            }
        </>
    );
};

export default React.memo(TableWrapperComponent, (prevProps: TableComponentProps, nextProps: TableComponentProps) => {
    if (prevProps && nextProps) {
        return isEqual(prevProps, nextProps); // Ensure the result is always a boolean
    }
    return false;
});

// ****************************** USAGE ****************************** //

// <TableWrapperComponent
//     columns={[
//         {
//             key: "name",
//             dataIndex: "name",
//             title: "Name"
//         },
//         {
//             key: "description",
//             dataIndex: "description",
//             title: "Description"
//         }
//     ]}
//     rowKey={(record: any) => {
//         return record._id;
//     }}
//     url={ENV.API_URL + "/category/637caaa4ef59e8a8cdc9f4b3/service"}
//     method={"get"}
//     isPaginated={true}
// />

// ****************************** USAGE ****************************** //
